import { FC } from "react";
import Link from "next/link";
import clsx from "clsx";
import { ArrowRight, CheckCircle, FileText } from "react-feather";
import { BlocksRenderer } from "@strapi/blocks-react-renderer";

import { IScqaExplainerSection } from "@/api/dtos/block-component.type";

// ----------------------------------------------------------------------------
// SCQA service-page explainer.
//
// Renders the Situation -> Complication -> Question -> Answer narrative the
// compliance audit found every service page was missing, plus what's-included
// cards, a deliverables table, an engagement timeline strip, a visible pricing
// signal, quantified proof cards, and CTAs. The `question` is emitted as an H2
// so it doubles as an AEO question heading; the answer's first paragraph is the
// snippet-liftable direct answer.
// ----------------------------------------------------------------------------

const FORMAT_LABEL: Record<string, string> = {
  pdf: "PDF",
  portal: "Live portal",
  letter: "Signed letter",
  spreadsheet: "Spreadsheet",
  presentation: "Presentation",
};

const AUDIENCE_LABEL: Record<string, string> = {
  board: "Board",
  engineering: "Engineering",
  auditor: "Auditor",
  compliance: "Compliance",
  all: "All stakeholders",
};

type IScqaExplainerSectionProps = {
  content: IScqaExplainerSection;
};

const ScqaExplainerSection: FC<IScqaExplainerSectionProps> = ({ content }) => {
  const {
    situation,
    complication,
    question,
    answer,
    whats_included,
    deliverables,
    timeline_phases,
    pricing_starting_at,
    pricing_scoping_factors,
    pricing_cost_page_link,
    proof,
    ctas,
  } = content;

  const heroCta = ctas?.find((c) => c.position === "hero");
  const midCta = ctas?.find((c) => c.position === "mid");
  const endCta = ctas?.find((c) => c.position === "end");

  const scopingFactors = Array.isArray(pricing_scoping_factors)
    ? pricing_scoping_factors
    : [];

  return (
    <section className="container py-12 lg:py-16 xl:py-20">
      {/* ---------- S / C / Q / A narrative ---------- */}
      <div className="mx-auto max-w-4xl">
        <div className="flex flex-col gap-4">
          {situation && (
            <p className="font-darker-grotesque text-lg font-normal leading-[1.6] text-black-700 md:text-h7">
              {situation}
            </p>
          )}
          {complication && (
            <p className="font-darker-grotesque text-lg font-normal leading-[1.6] text-[#a50104] md:text-h7">
              {complication}
            </p>
          )}
        </div>

        {question && (
          <h2 className="mt-6 font-bricolage-grotesque text-[clamp(1.5rem,2.5vw+1rem,2.75rem)] font-medium !leading-[1.1] text-black-900 [text-wrap:balance]">
            {question}
          </h2>
        )}

        {answer && (
          <div
            className={clsx(
              "mt-4",
              "[&_p]:!font-darker-grotesque [&_p]:!text-[clamp(1.0625rem,0.5vw+0.9375rem,1.25rem)] [&_p]:!font-normal [&_p]:!leading-[1.7] [&_p]:!text-black-800 [&_p]:mb-3",
              "[&_strong]:!font-medium [&_strong]:!text-black-900",
              "[&_a]:!text-primary-900 [&_a]:underline",
            )}
          >
            <BlocksRenderer content={answer} />
          </div>
        )}

        {heroCta && (
          <div className="mt-6">
            <Link
              href={heroCta.href}
              id={`scqa-cta-hero-${heroCta.intent}`}
              className="group inline-flex h-12 items-center justify-center gap-2 rounded-full bg-[#FB441B] px-7 font-bricolage-grotesque text-base font-medium text-white shadow-sm transition-all hover:bg-[#E03A17] hover:shadow-[0_10px_25px_rgba(251,68,27,0.3)]"
            >
              {heroCta.label}
              <ArrowRight
                size={18}
                className="transition-transform duration-300 group-hover:translate-x-0.5"
              />
            </Link>
          </div>
        )}
      </div>

      {/* ---------- What's included ---------- */}
      {whats_included && whats_included.length > 0 && (
        <div className="mx-auto mt-12 max-w-6xl lg:mt-16">
          <h3 className="mb-6 font-bricolage-grotesque text-h5 font-medium text-black-900 md:text-3xl">
            What&apos;s included
          </h3>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 lg:gap-6">
            {whats_included.map((item) => (
              <div
                key={item.id}
                className="rounded-xl border border-black-100 bg-white p-6"
              >
                <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-primary-50 text-primary-900">
                  <CheckCircle size={18} strokeWidth={2.2} />
                </div>
                <h4 className="font-bricolage-grotesque text-lg font-medium !leading-[1.25] text-black-900">
                  {item.title}
                </h4>
                <p className="mt-2 font-darker-grotesque text-base font-normal leading-[1.6] text-black-700 md:text-[17px]">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ---------- Engagement timeline ---------- */}
      {timeline_phases && timeline_phases.length > 0 && (
        <div className="mx-auto mt-12 max-w-6xl lg:mt-16">
          <h3 className="mb-6 font-bricolage-grotesque text-h5 font-medium text-black-900 md:text-3xl">
            How the engagement runs
          </h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {timeline_phases.map((phase, idx) => (
              <div
                key={phase.id}
                className="relative rounded-xl border border-black-100 bg-white p-5"
              >
                <span className="font-space-grotesk text-sm font-semibold text-primary-900">
                  {String(idx + 1).padStart(2, "0")}
                </span>
                <h4 className="mt-2 font-bricolage-grotesque text-lg font-medium !leading-[1.25] text-black-900">
                  {phase.phase_name}
                </h4>
                <p className="mt-1 font-space-grotesk text-sm font-medium text-primary-900">
                  {phase.duration_value} {phase.duration_unit}
                </p>
                {phase.milestone && (
                  <p className="mt-2 font-darker-grotesque text-[15px] font-normal leading-[1.5] text-black-600">
                    {phase.milestone}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ---------- Deliverables table ---------- */}
      {deliverables && deliverables.length > 0 && (
        <div className="mx-auto mt-12 max-w-4xl lg:mt-16">
          <h3 className="mb-6 font-bricolage-grotesque text-h5 font-medium text-black-900 md:text-3xl">
            What you get
          </h3>
          <div className="overflow-hidden rounded-xl border border-black-100">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-primary-50">
                  <th className="px-5 py-3 text-left font-bricolage-grotesque text-xs font-semibold uppercase tracking-[0.1em] text-primary-900">
                    Deliverable
                  </th>
                  <th className="px-5 py-3 text-left font-bricolage-grotesque text-xs font-semibold uppercase tracking-[0.1em] text-primary-900">
                    Format
                  </th>
                  <th className="px-5 py-3 text-left font-bricolage-grotesque text-xs font-semibold uppercase tracking-[0.1em] text-primary-900">
                    For
                  </th>
                </tr>
              </thead>
              <tbody>
                {deliverables.map((d) => (
                  <tr
                    key={d.id}
                    className="border-t border-black-100 bg-white"
                  >
                    <td className="px-5 py-4 font-darker-grotesque text-base font-medium text-black-900">
                      {d.sample_link ? (
                        <Link
                          href={d.sample_link}
                          className="inline-flex items-center gap-1.5 text-primary-900 underline"
                        >
                          <FileText size={15} />
                          {d.artifact_name}
                        </Link>
                      ) : (
                        d.artifact_name
                      )}
                    </td>
                    <td className="px-5 py-4 font-darker-grotesque text-base font-normal text-black-700">
                      {FORMAT_LABEL[d.format] ?? d.format}
                    </td>
                    <td className="px-5 py-4 font-darker-grotesque text-base font-normal text-black-700">
                      {AUDIENCE_LABEL[d.audience] ?? d.audience}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {midCta && (
            <div className="mt-5">
              <Link
                href={midCta.href}
                id={`scqa-cta-mid-${midCta.intent}`}
                className="group inline-flex items-center gap-1.5 font-bricolage-grotesque text-sm font-medium text-primary-900 transition-colors hover:text-primary-800"
              >
                {midCta.label}
                <ArrowRight
                  size={15}
                  className="transition-transform duration-200 group-hover:translate-x-0.5"
                />
              </Link>
            </div>
          )}
        </div>
      )}

      {/* ---------- Proof ---------- */}
      {proof && proof.length > 0 && (
        <div className="mx-auto mt-12 max-w-6xl lg:mt-16">
          <h3 className="mb-6 font-bricolage-grotesque text-h5 font-medium text-black-900 md:text-3xl">
            Proof
          </h3>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 lg:gap-6">
            {proof.map((p) => (
              <div
                key={p.id}
                className="relative overflow-hidden rounded-xl border border-black-100 bg-white p-6"
              >
                <span
                  aria-hidden="true"
                  className="absolute inset-x-0 top-0 h-1.5 bg-yellow-400"
                />
                <span className="font-mono text-[11px] font-normal uppercase tracking-[0.12em] text-black-500">
                  {p.client_type}
                </span>
                <p className="mt-2 font-space-grotesk text-[clamp(2rem,2vw+1.25rem,3rem)] font-semibold !leading-none text-primary-900">
                  {p.headline_metric}
                </p>
                <p className="mt-3 font-darker-grotesque text-base font-normal leading-[1.6] text-black-700 md:text-[17px]">
                  {p.outcome}
                </p>
                {p.link && (
                  <Link
                    href={p.link}
                    className="mt-3 inline-flex items-center gap-1.5 font-bricolage-grotesque text-sm font-medium text-primary-900 transition-colors hover:text-primary-800"
                  >
                    Read the case study
                    <ArrowRight size={14} />
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ---------- Pricing signal ---------- */}
      {pricing_starting_at && (
        <div className="mx-auto mt-12 max-w-4xl lg:mt-16">
          <div className="rounded-2xl border border-primary-100 bg-primary-50 p-6 md:p-8">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <span className="font-mono text-[11px] font-normal uppercase tracking-[0.12em] text-primary-900">
                  Investment
                </span>
                <p className="mt-1 font-bricolage-grotesque text-h5 font-medium text-black-900 md:text-3xl">
                  {pricing_starting_at}
                </p>
                {scopingFactors.length > 0 && (
                  <p className="mt-2 font-darker-grotesque text-base font-normal text-black-700">
                    Final quote depends on: {scopingFactors.join(" · ")}
                  </p>
                )}
              </div>
              {pricing_cost_page_link && (
                <Link
                  href={pricing_cost_page_link}
                  className="group inline-flex h-11 shrink-0 items-center justify-center gap-2 rounded-full border border-primary-200 bg-white px-6 font-bricolage-grotesque text-sm font-medium text-primary-900 transition-all hover:border-primary-300 hover:shadow-sm"
                >
                  See full pricing
                  <ArrowRight
                    size={15}
                    className="transition-transform duration-200 group-hover:translate-x-0.5"
                  />
                </Link>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ---------- Closing CTA ---------- */}
      {endCta && (
        <div className="mx-auto mt-10 max-w-4xl text-center lg:mt-12">
          <Link
            href={endCta.href}
            id={`scqa-cta-end-${endCta.intent}`}
            className="group inline-flex h-12 items-center justify-center gap-2 rounded-full bg-primary-900 px-8 font-bricolage-grotesque text-base font-medium text-white shadow-sm transition-all hover:bg-primary-800 hover:shadow-[0_10px_25px_rgba(5,74,218,0.25)]"
          >
            {endCta.label}
            <ArrowRight
              size={18}
              className="transition-transform duration-300 group-hover:translate-x-0.5"
            />
          </Link>
        </div>
      )}
    </section>
  );
};

export default ScqaExplainerSection;
